import React, { useState, useEffect, useContext, useRef } from "react";
import { Table, Tooltip, Typography } from "antd";
import { useNavigate } from "react-router-dom";
import { getTablePagination } from "../../../../utils/tablePagination";
import useQuery from "../../../../hooks/useQuery";
import {
  parsePageParam,
  updateUrlSearchParams,
} from "../../../../helpers/urlFilters";
import AssignServiceModal from "./AssignServiceModal";
import { getFacilityServicesApi } from "../../../../services/service.services";
import { getCookie } from "../../../../helpers/localStorage";
import { UserContext } from "../../../../context/UserContext";
import { FacilityID } from "../../../../constants/defaultKeys";
import { formatToINR } from "../../../../helpers/utility";
import { UiButton, UiDataTable } from "../../../ui";
import useIsMobile from "../../../../hooks/useIsMobile";
import usePageFillTableScroll from "../../../../hooks/usePageFillTableScroll";

const { Paragraph } = Typography;

const ServicesIpTable = ({
  patientData,
  searchQuery = "",
  categoryFilter = null,
  onCountChange,
}) => {
  const { userDetails } = useContext(UserContext);
  const navigate = useNavigate();
  const params = useQuery();
  const page = parsePageParam(params, 1);
  const isMobile = useIsMobile();
  const tableWrapRef = useRef(null);
  const tableScroll = usePageFillTableScroll(tableWrapRef, isMobile, "services");
  const [services, setServices] = useState([]);
  const [open, setOpen] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [editData, setEditData] = useState(null);
  const [loading, setLoading] = useState(false);

  const admission = patientData?.admission?.is_active;

  const refreshServices = () => {
    setRefresh((prev) => !prev);
  };

  useEffect(() => {
    let param = {
      ip_admission: params?.patient,
      page: searchQuery ? 1 : page,
      limit: 100,
    };
    if (searchQuery) {
      param.search = searchQuery;
    }
    if (categoryFilter) {
      param.category = categoryFilter;
    }
    const queryString = new URLSearchParams(param);
    setLoading(true);
    getFacilityServicesApi(
      Number(getCookie(FacilityID)) || userDetails?.currFacility,
      queryString
    )
      .then((res) => {
        setServices(res?.data);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
      });
  }, [
    refresh,
    searchQuery,
    categoryFilter,
    page,
    params?.patient,
    userDetails?.currFacility,
  ]);

  const displayCount = services?.total_count ?? 0;

  useEffect(() => {
    onCountChange?.(displayCount);
  }, [displayCount, onCountChange]);

  const columns = [
    {
      title: "Service Name",
      dataIndex: "name",
      key: "name",
      width: isMobile ? 120 : 180,
      fixed: isMobile ? "left" : undefined,
      ellipsis: true,
      onCell: () => ({ style: { maxWidth: isMobile ? 120 : 180 } }),
      render: (value) => {
        const name = value || "—";
        if (!isMobile) {
          return (
            <Typography.Text
              style={{ display: "inline-block", maxWidth: "100%" }}
              ellipsis={{ tooltip: name }}
            >
              {name}
            </Typography.Text>
          );
        }
        return (
          <Tooltip title={name} trigger={["click"]} placement="topLeft">
            <Typography.Text
              style={{ display: "inline-block", maxWidth: "100%" }}
              ellipsis
            >
              {name}
            </Typography.Text>
          </Tooltip>
        );
      },
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
      render: (value) => (
        <Paragraph
          ellipsis={{ rows: 2, expandable: true, symbol: "more" }}
          style={{ marginBottom: 0 }}
        >
          {value || "—"}
        </Paragraph>
      ),
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      width: 140,
      ellipsis: true,
      render: (_, record) => <span>{record?.category?.name}</span>,
    },
    {
      title: "Price",
      dataIndex: "cash_price",
      key: "cash_price",
      width: 120,
      render: (value) => formatToINR(value),
    },

    ...(admission
      ? [
          {
            title: "Action",
            dataIndex: "action",
            key: "action",
            width: 110,
            align: "center",
            render: (_, record) => (
              <UiButton
                variant="secondary"
                size="sm"
                onClick={() => {
                  setOpen(true);
                  setEditData(record);
                }}
              >
                Assign
              </UiButton>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <UiDataTable showSearch={false} showHeader={false} shellClassName="ui-table-fill-shell">
        <div ref={tableWrapRef}>
          <Table
            className="no-vertical-borders"
            tableLayout="fixed"
            dataSource={services?.data}
            columns={columns}
            pagination={getTablePagination({
              current: page,
              pageSize: 100,
              total: services?.total_count ?? 0,
              onChange: (p) => updateUrlSearchParams(navigate, { page: p }),
            })}
            loading={loading}
            scroll={isMobile ? { x: 900 } : tableScroll}
            bordered={false}
          />
        </div>
      </UiDataTable>
      {open && (
        <AssignServiceModal
          refresh={refreshServices}
          open={open}
          setOpen={setOpen}
          editData={editData}
        />
      )}
    </>
  );
};

export default ServicesIpTable;
